<template>
    <div v-if="allowed.read" class="cedex-dependencies-root flex-root-row">
        <div class="flex-root-column editor-box">
            <!-- Header -->
            <div class="editor-table cedex-codes" ref="cedexCodeTable">
                <table class="vcs-table">
                    <tr>
                        <!-- <th class="vcs-table-header">#</th> -->
                        <th
                            v-for="header in columns"
                            :key="header.id"
                            v-show="visibleColumns[header.id]"
                            class="vcs-table-header"
                            :class="(ownSortingColumn === header.id ? 'sorted' + (ownSortingDirection === 'asc' ? ' sorted-ascending' : ' sorted-descending' ) : '')"
                            :style="(header.width ? 'width: ' + header.width + 'px;' : '')"
                            @click="changeSorting(header.id)"
                        >
                            <div class="vcs-table-header-title">{{ $t(header.title) }}{{
                                header.required ? ' *' : ''
                            }}</div>
                            <div
                                v-show="header.filter"
                                class="vcs-table-header-filter"
                                :class="{active: activeFilterValues[header.id] && activeFilterValues[header.id].length}"
                                @click.stop="showFilter(header.id)"
                                :title="filterTitle(header.id)"
                            />
                        </th>
                    </tr>
                    <tr
                        v-if="codes.length === 0"
                        class="no-data">
                        <td :colspan="columns.length">-- {{ $t('no CEDEX configuration found') }} --<br/>
                            <div class="add-first-line" @click="addRow(-1, 'isoCode')">{{ $t('add data') }}</div>
                        </td>
                    </tr>
                    <tr
                        v-else-if="codes.length === 0"
                        class="no-data">
                        <td :colspan="columns.length">-- {{ $t('no data loaded. Please select a primary filter.') }} --</td>
                    </tr>
                    <tr
                        v-for="(codeData, index) in paginatedCodeData"
                        :key="codeData.code + '_' + index"
                    >
                        <!-- <td>{{index}}</td> -->
                        <td
                            v-for="column in Object.keys(visibleColumns).filter(column => visibleColumns[column] && textColumns.includes(column))"
                            :key="column + '_' + codeData[column] + '_' + index"
                            v-show="visibleColumns[column]"
                        >
                            <div v-if="!allowed.write">{{codeData[column]}}</div>
                            <v-text-field
                                v-else
                                :value="codeData[column]"
                                :class="{error: validationErrors[column] && validationErrors[column][index + paginationOffset]}"
                                @change="changeData(index, column, $event)"
                            >{{codeData[column]}}</v-text-field>
                        </td>

                        <td
                            v-if="visibleColumns.equipment"
                        ><v-select
                            :items="equipments"
                            multiple
                            :value="codeData.equipment"
                            @change="changeData(index, 'equipment', $event)"
                        /></td>

                        <td
                            v-if="visibleColumns.active"
                        ><img class="boolean" :class="{readonly: !allowed.write}"
                              :src="require('@/assets/icons/check_box_' + (codeData.active === '1' ? '' : 'un') + 'checked.svg')"
                              @click="changeData(index, 'active', codeData.active === '1' ? '0' : '1')"
                              alt="">
                        </td>

                        <td v-if="visibleColumns.actions">
                            <img v-if="allowed.write" class="boolean delete" src="@/assets/icons/table/bin.svg" @click="removeRow(index)" alt="">
                        </td>
                    </tr>
                </table>
            </div>
            <Pagination
                :pageCount="pageCount"
                :page-size="pageSize"
                :visible-buttons="7"
                v-model="currentPage"
                @input="setScrollTop"
                @pageSizeChange="setPaginationChange"
            />
            <div v-show="loading" class="loading"></div>
        </div>

        <TableFilters
            ref="codeFilters"
            :filters="filters"
            @change="onFiltersChange"
        />
    </div>
</template>

<script>
import Pagination from "../Table/Pagination.vue";
import i18n from '../../i18n';
import TableFilters from "../Table/Filter/TableFilters.vue"
import authorization from "../../plugins/authorization";
import axios from "axios";
import equipments , {popupShowTime} from "../../plugins/constants";

export default {
    name: "CedexCodeEditor",
    components: {
        Pagination,
        TableFilters
    },
    props: {
        // view data selection
        codeType: String,
        columns:  Array,

        sortingColumn: {
            type:    String,
            default: null
        },
        sortingDirection: {
            type:    String,
            default: null
        },
    },
    created() {
        this.updateView('codeType', this.selectedCodeType);
    },
    data: function () {
        return {
            selectedCodeType:         this.codeType,
            codes:                    [],
            removedCodes:             [],
            codeCorrectionMap:        {
                location:  4,
                component: 3,
                default:   2
            },
            loading:                  false,
            validationErrors:         {},

            // Pagination
            paginationVisibleButtons: 7,
            currentPage:              1,
            pageSize:                 100,
            // Sorting
            ownSortingColumn:         this.sortingColumn,
            ownSortingDirection:      this.sortingDirection,

            icon:                     'mdi-checkbox-blank-outline',
            selectValues:             {},

            activeFilterValues:       {
                code:        '',
                name:        '',
                description: '',
                active:      null,
            },
            activeFilter:             false,
            availableFilterValues:    {},

            allowed: {
                read:  authorization.isAllowed('Settings/CEDEX Settings/Dependencies/Editor/View'),
                write: authorization.isAllowed('Settings/CEDEX Settings/Dependencies/Editor/View', authorization.write),
            },

            textColumns: [
                'code',
                'name',
                'description',
                'nameDe',
                'descriptionDe',
                'nameRu',
                'descriptionRu',
            ],

            equipments: Object.values(equipments)

        }
    },
    watch: {
        sortingColumn: function () {
            this.ownSortingColumn = this.sortingColumn;
        },
        sortingDirection: function () {
            this.ownSortingDirection = this.sortingDirection;
        }
    },
    methods: {
        loadData() {
            axios
                .get(
                    'cedexCodes',
                    {
                        params : {
                            codeType: this.selectedCodeType,
                            filter: this.activeFilterValues,
                            sorting: {
                                column:    this.ownSortingColumn,
                                direction: this.ownSortingDirection
                            }
                        }
                    }
                )
                .then(response => {

                    this.codes = response.data;

                    return response;
                }).catch(error => {
                    console.log(error);
                    this.showMessage(i18n.t('Codes loading failed.'), 'error', popupShowTime);
                }).then(() => {
                    this.dataLoaded = true;
                    this.hideLoading();
                    let codeTypeName = this.selectedCodeType.charAt(0).toUpperCase() + this.selectedCodeType.slice(1);
                    this.showMessage(i18n.t(codeTypeName + ' Codes loaded.'), 'success', popupShowTime);
                });
        },
        showMessage(message, state, time) {
            this.$emit('showMessage', message, state, time);
        },
        updateView(type, value) {
            if (type === 'displayOtherLanguage') {
                this.columns.forEach((column) => {
                    if (this.otherLanguageColumns.includes(column.id)) {
                        column.visible = value;
                    }
                });
            } else if (
                type === 'codeType'
                && (
                    !this.dataLoaded
                    || this.selectedCodeType !== value
                )
            ) {
                this.columns.forEach((column) => {
                    if (column.id === 'equipment') {
                        column.visible = value === 'component';
                        return false;
                    }
                });
                this.selectedCodeType = value;
                this.dataLoaded = false;
                this.showLoading();
                this.currentPage = 1;
                this.loadData();
            }
        },
        filterTitle(title) { return title; },
        setScrollTop() {
            this.$refs.cedexCodeTable.scrollTo(0, 0);
        },
        setPaginationChange(pageSize) {
            this.pageSize = pageSize;
            if (this.currentPage > this.pageCount) {
                this.currentPage = this.pageCount;
            }
        },
        changeSorting(columnId) {
            // every tree column is sorted ascending by default
            // so change sorting of one of these columns is only possible to descending
            // revert the descending sorting means the column is sorted ascending again
            // all columns after inspection criteria can be sorted ascending or
            if (this.ownSortingColumn === columnId) {
                if (this.ownSortingDirection === 'desc') {
                    this.ownSortingDirection = 'asc';
                } else {
                    this.ownSortingDirection = 'desc';
                }
            } else {
                this.ownSortingDirection = 'desc';
            }

            this.ownSortingColumn = columnId;

            this.loadData();
        },
        showFilter(id) {
            this.$refs.codeFilters.activeFilter = id;
        },
        onFiltersChange(filterData) {
            if (JSON.stringify(this.activeFilterValues) === JSON.stringify(filterData)) {
                return false;
            }

            Object.assign(this.activeFilterValues, filterData);
            this.loadData()
        },
        showLoading() {
            this.loading = true;
        },
        hideLoading() {
            this.loading = false;
        },
        async removeRow(index) {

            if (!this.allowed.write) {
                return;
            }

            const res = await this.$dialog.confirm({
                text: i18n.t('Do you really want to remove this dependency line?'),
                title: i18n.t('Delete CEDEX Dependency'),
                persistent: true,
                actions: {
                    false: i18n.t('No'),
                    true: i18n.t('Yes'),
                },
            });

            if (!res) {
                return;
            }

            index = index + this.paginationOffset;
            this.removedCodes.push(this.codes[index].code);
            this.codes.splice(index, 1);
            window.showBeforeUnload = true;
        },
        addNewCode() {
            let insertIndex = 0;
            let line = {
                code:           '',
                originalCode:   '',
                name:           'New Code',
                name_de:        '',
                name_ru:        '',
                description:    '',
                description_de: '',
                description_ru: '',
                active:         '0',
                changed:        '1',
            };

            // add line to data, correct insert index with pagination data
            this.codes.splice(insertIndex, 0, line);

            // scroll into view and mark new line
            this.$nextTick(() => {
                let newLineElement = this.$el.querySelector('div.editor-table table tr:nth-child(' + (insertIndex - this.paginationOffset + 2) + ')');
                newLineElement.scrollIntoView({
                    block:    'end',
                    behavior: 'smooth'
                });
                newLineElement.classList.add('newLine');
            });

            window.showBeforeUnload = true;
        },
        changeData(index, column, value) {
            index += this.paginationOffset;
            this.codes[index][column] = value;
            this.codes[index].changed = '1';

            window.showBeforeUnload = true;
        },
        async saveCodes() {
            const res = await this.$dialog.confirm({
                text:       i18n.t('Do you really want to save your current settings?'),
                title:      i18n.t('Save CEDEX Codes'),
                persistent: true,
                actions: {
                    false: 'No',
                    true:  'Yes',
                },
            });

            if (!res) {
                return;
            }

            this.showLoading();

            // validate
            if (!this.validateData()) {
                this.hideLoading();
                return;
            }

            let params = new FormData();
            params.append('codeType', this.codeType);
            params.append('codes', JSON.stringify(this.codes));
            params.append('removedCodes', this.removedCodes.join(','));

            axios
                .post('cedexCodes', params)
                .then(response => {
                    this.hideLoading();
                    if (
                        response.status === 200
                        && response.data.success
                    ) {
                        this.showMessage(i18n.t('Codes saved.'), 'success', popupShowTime);
                        window.showBeforeUnload = false;
                        // reload data
                        this.updateView('layoutValue', this.layoutValue);
                    } else {
                        let message = i18n.t('Codes saving failed.');
                        if (response.data.message) {
                            message += ' ' + response.data.message;
                        }
                        this.showMessage(message, 'error', popupShowTime);
                    }
                })
                .catch(error => {
                    this.$refs.cedexDependencies.hideLoading();
                    this.showMessage(i18n.t('Codes saving failed.'), 'error', popupShowTime);
                    console.log(error);
                });
        },
        validateData() {
            this.validationErrors = {
                code: {},
                name: {}
            };
            let validationMessages = [];
            this.codes.forEach((code, index) => {
                let line = (this.paginationOffset ? index % this.paginationOffset : index) + 1;
                let page = this.paginationOffset ? Math.floor(index / this.paginationOffset) + 1 : 1;

                let codeLength = this.codeCorrectionMap[this.selectedCodeType] || this.codeCorrectionMap.default;
                let codeValue = (code.code + '').trim();
                if (codeValue.length !== codeLength) {
                    this.validationErrors.code[index] = true;
                    let message = i18n.t(
                        'Page {page}, Line {line}', {
                            line: line,
                            page: page
                        }
                    ) + ': ' + i18n.t('The code has to be exact {length} characters long.', {length: codeLength});
                    validationMessages.push(message);
                }
                let name = (code.name + '').trim();
                if (name === '') {
                    this.validationErrors.name[index] = true;
                    let message = i18n.t(
                        'Page {page}, Line {line}', {
                            line: line,
                            page: page
                        }
                    ) + ': ' + i18n.t('The code needs a valid name.');
                    validationMessages.push(message);
                }
            });

            this.$emit('showMessage', validationMessages.join("\n"));

            return validationMessages.length === 0;
        },
        setAvailableFilterValues(filters) {
            let availableFilterValues = {};
            Object.keys(filters).forEach((name) => {
                availableFilterValues[name] = filters[name];
            });
            this.availableFilterValues = availableFilterValues;
        }
    },
    computed: {
        paginatedCodeData() {
            if (this.codes.length === 0) {
                return [];
            }
            let start = (this.currentPage - 1) * this.pageSize;
            let end = Math.min(this.currentPage * this.pageSize, this.codes.length);
            let firstLine = Object.assign({}, this.codes[start]);

            let paginatedData = [firstLine];
            for (let i = start + 1; i < end; i++) {
                paginatedData.push(Object.assign({}, this.codes[i]));
            }

            return paginatedData;
        },
        visibleColumns() {
            let visibleColumns = [];
            for (let columnIndex in this.columns) {
                visibleColumns[this.columns[columnIndex].id] = this.columns[columnIndex].visible;
            }
            return visibleColumns;
        },
        columnOrder() {
            let columnOrder = [];
            for (let columnIndex in this.columns) {
                columnOrder.push(this.columns[columnIndex].id);
            }
            return columnOrder;
        },
        pageCount() {
            return Math.ceil(this.codes.length / this.pageSize);
        },
        paginationOffset() {
            return (this.currentPage - 1) * this.pageSize;
        },
        filters() {
            let filters = [];

            for (let key in this.columns) {
                let column = this.columns[key];
                if (
                    column.id === 'actions'
                    || !column.visible
                ) {
                    continue;
                }
                let filter = {
                    name:  column.id,
                    title: column.title,
                    type:  'string',
                };

                if (column.id === 'equipment') {
                    filter.type = 'data';
                    filter.data = this.equipments.map((equipment) => ({
                        id:   equipment,
                        name: equipment,
                    }));
                } else if (column.id === 'active') {
                    filter.type = 'boolean';
                    filter.data = [
                        {id: 0, name: i18n.t('unset')},
                        {id: 1, name: i18n.t('active')},
                        {id: 2, name: i18n.t('inactive')},
                    ];
                }

                filters.push(filter);
            }

            return filters;
        }

    }
}
</script>

<style>

    div.editor-table.cedex-codes table.vcs-table td {
        padding: 0;
    }
    td div.v-input.v-text-field {
        padding: 0;
        margin: 0;
    }
    td div.v-input.v-text-field div.v-input__control div.v-input__slot {
        margin: 0;
    }
    td div.v-input.v-text-field div.v-input__control div.v-input__slot:after {
        border: none
    }
    td div.v-input.v-text-field div.v-input__control div.v-input__slot div.v-text-field__slot input {
        padding: 8px;
        font-size: 14px;
    }
    td div.v-input.v-text-field div.v-input__control div.v-text-field__details {
        display: none;
    }
</style>
